import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { virtualH2hAPI } from '../api/endpoints';
import {
  FaFire, FaUsers, FaTrophy, FaSearch, FaSync,
  FaClock, FaRobot, FaUserTie, FaFilter, FaTimes,
  FaCheckCircle, FaTimesCircle, FaUndo, FaMoneyBillWave, FaHistory,
  FaExclamationTriangle
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './VirtualH2H.css';

const TICK = 1000;

const FILTERS = [
  { key: 'all',     label: 'All',      icon: FaFire },
  { key: 'system',  label: 'System',   icon: FaRobot },
  { key: 'creator', label: 'Creators', icon: FaUserTie },
];

// Helper: fetch both sources in parallel, tag each with `source`
const fetchFeedSources = async () => {
  const [autoRes, creatorRes] = await Promise.allSettled([
    virtualH2hAPI.list(),
    (async () => {
      const mod = await import('../api/endpoints');
      if (mod.creatorVh2hAPI?.listPublic) {
        return mod.creatorVh2hAPI.listPublic();
      }
      return { data: { data: [] } };
    })(),
  ]);

  const autoGames = autoRes.status === 'fulfilled'
    ? (autoRes.value?.data?.data || []).map((g) => ({ ...g, source: 'system' }))
    : [];

  const creatorGames = creatorRes.status === 'fulfilled'
    ? (creatorRes.value?.data?.data || []).map((g) => ({ ...g, source: 'creator' }))
    : [];

  return [...autoGames, ...creatorGames];
};

const VirtualH2H = () => {
  const { isAuthenticated } = useAuth();

  // Feed state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [games, setGames] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [tick, setTick] = useState(0);

  // Tabs
  const [activeTab, setActiveTab] = useState('all');

  // My history
  const [history, setHistory] = useState({ summary: null, items: [] });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // ==================== FETCH FEED ====================
  const fetchFeed = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const feed = await fetchFeedSources();
      setGames(feed);
    } catch (err) {
      console.error('[VirtualH2H] feed fetch failed:', err);
      if (showLoader) toast.error('Failed to load virtual games');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  // ==================== FETCH MY HISTORY ====================
 const fetchHistory = useCallback(async (showLoader = false) => {
  if (!isAuthenticated) {
    setHistoryError('Please log in to see your games.');
    return;
  }
  if (showLoader) setHistoryLoading(true);
  setHistoryError(null);

  try {
    const res = await virtualH2hAPI.getMyHistory({ limit: 50 });

    let data = res?.data;

    // If the response is a string (HTML prefix leaked into the body),
    // strip everything before the first '{' and parse manually.
    if (typeof data === 'string') {
      const jsonStart = data.indexOf('{');
      if (jsonStart === -1) {
        throw new Error('Invalid response from server');
      }
      const cleaned = data.slice(jsonStart);
      try {
        data = JSON.parse(cleaned);
      } catch (parseErr) {
        console.error('[VirtualH2H] Could not parse history response:', parseErr, cleaned);
        throw new Error('Response was not valid JSON');
      }
    }

    // Normalize shape
    let summary =
      data?.summary ||
      data?.data?.summary ||
      null;
    let items =
      Array.isArray(data?.items) ? data.items :
      Array.isArray(data?.data) ? data.data :
      Array.isArray(data?.data?.items) ? data.data.items :
      [];

    console.log('[VirtualH2H] history parsed:', { summary, itemCount: items.length });

    setHistory({ summary, items });
  } catch (err) {
    console.error('[VirtualH2H] history fetch failed:', err);
    const status = err?.response?.status;
    const msg = err?.response?.data?.message || err?.message;
    setHistoryError(
      status === 401 ? 'Session expired — please log in again.' :
      status === 404 ? 'History endpoint not found on the server.' :
      status === 500 ? 'Server error loading your history.' :
      msg || 'Failed to load your history.'
    );
    setHistory({ summary: null, items: [] });
  } finally {
    if (showLoader) setHistoryLoading(false);
  }
}, [isAuthenticated]);

  // Initial load
  useEffect(() => {
    fetchFeed(true);
  }, [fetchFeed]);

  // Fetch history when tab switches to "mine"
  useEffect(() => {
    if (activeTab === 'mine') fetchHistory(true);
  }, [activeTab, fetchHistory]);

  // Poll feed every 15s + local ticker for countdowns
  useEffect(() => {
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') fetchFeed(false);
    }, 15000);
    const clock = setInterval(() => setTick((n) => n + 1), TICK);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [fetchFeed]);

  // Refresh history every 20s while the "mine" tab is active
  useEffect(() => {
    if (activeTab !== 'mine') return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') fetchHistory(false);
    }, 20000);
    return () => clearInterval(t);
  }, [activeTab, fetchHistory]);

  const handleRefresh = async () => {
    setRefreshing(true);
    if (activeTab === 'all') await fetchFeed(false);
    else await fetchHistory(true);
    setRefreshing(false);
  };

  const clearFilters = () => {
    setFilter('all');
    setSearchTerm('');
  };

  // ==================== FILTER + SEARCH + SORT ====================
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return games
      .filter((g) => (filter === 'all' ? true : g.source === filter))
      .filter((g) => {
        if (!term) return true;
        const haystack = [g.home_team, g.away_team, g.creator_name, g.league]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      })
      .sort((a, b) => {
        const order = { live: 0, open: 1, completed: 2 };
        const oa = order[a.status] ?? 3;
        const ob = order[b.status] ?? 3;
        if (oa !== ob) return oa - ob;
        const ta = a.join_closes_at ? new Date(a.join_closes_at).getTime() : 0;
        const tb = b.join_closes_at ? new Date(b.join_closes_at).getTime() : 0;
        return ta - tb;
      });
  }, [games, filter, searchTerm]);

  const totalCounts = useMemo(() => ({
    all: games.length,
    system: games.filter((g) => g.source === 'system').length,
    creator: games.filter((g) => g.source === 'creator').length,
  }), [games]);

  const hasActiveFilters = filter !== 'all' || searchTerm.trim() !== '';

  // ==================== RENDER ====================
  if (loading) {
    return (
      <div className="vh2h-feed-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="vh2h-feed-page">
      {/* HERO */}
      <div className="vh2h-feed-hero">
        <div>
          <h1><FaFire className="hero-icon" /> Virtual H2H</h1>
          <p>Auto-generated and creator-hosted virtual matches. Predict. Win. Repeat.</p>
        </div>
        <button
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh"
          type="button"
        >
          {refreshing ? <LoadingSpinner size="small" /> : <FaSync />}
        </button>
      </div>

      {/* TABS */}
      <div className="vh2h-tabs">
        <button
          className={`vh2h-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
          type="button"
        >
          <FaFire /> All Games
        </button>
        <button
          className={`vh2h-tab ${activeTab === 'mine' ? 'active' : ''}`}
          onClick={() => setActiveTab('mine')}
          type="button"
        >
          <FaHistory /> My Games
          {history.summary?.unclaimed > 0 && (
            <span className="tab-badge">
              {Number(history.summary.unclaimed).toFixed(2)} $XPUNT
            </span>
          )}
        </button>
      </div>

      {/* ==================== ALL GAMES TAB ==================== */}
      {activeTab === 'all' && (
        <>
          <div className="vh2h-feed-controls">
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by team, creator, or league..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="vh2h-search-input"
              />
              {searchTerm && (
                <button
                  className="search-clear"
                  onClick={() => setSearchTerm('')}
                  type="button"
                  aria-label="Clear search"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <div className="filter-row">
              <span className="filter-label"><FaFilter /> Source:</span>
              {FILTERS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  className={`filter-chip ${filter === key ? 'active' : ''}`}
                  onClick={() => setFilter(key)}
                  type="button"
                >
                  <Icon />
                  <span>{label}</span>
                  <span className="filter-count">{totalCounts[key]}</span>
                </button>
              ))}
              {hasActiveFilters && (
                <button className="filter-clear" onClick={clearFilters} type="button">
                  <FaTimes /> Clear
                </button>
              )}
            </div>
          </div>

          <div className="vh2h-feed-results-header">
            <span>
              {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
              {searchTerm && <> for "<strong>{searchTerm}</strong>"</>}
            </span>
          </div>

          {filtered.length === 0 ? (
            <div className="vh2h-feed-empty">
              <FaFire className="empty-icon" />
              <h3>No matches found</h3>
              <p>
                {hasActiveFilters
                  ? 'Try adjusting your search or filters.'
                  : 'No virtual matches right now. Check back in a few minutes — the system drops new batches regularly.'}
              </p>
              {hasActiveFilters && (
                <button className="btn btn-primary" onClick={clearFilters}>
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="vh2h-feed-grid">
              {filtered.map((g) => (
                <VirtualMatchCard
                  key={`${g.source}-${g.id}`}
                  game={g}
                  isAuthenticated={isAuthenticated}
                  onRefresh={() => fetchFeed(false)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ==================== MY GAMES TAB ==================== */}
      {activeTab === 'mine' && (
        <>
          {historyLoading ? (
            <div className="vh2h-feed-loading"><LoadingSpinner size="large" /></div>
          ) : historyError ? (
            <div className="vh2h-feed-empty">
              <FaExclamationTriangle className="empty-icon" />
              <h3>Couldn't load your games</h3>
              <p>{historyError}</p>
              <button
                className="btn btn-primary"
                onClick={() => fetchHistory(true)}
                type="button"
              >
                <FaSync /> Try Again
              </button>
            </div>
          ) : (
            <>
              {history.summary && history.summary.total_played > 0 && (
                <div className="my-games-summary">
                  <div className="summary-stat">
                    <span className="stat-label">Played</span>
                    <span className="stat-value">{history.summary.total_played}</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Won</span>
                    <span className="stat-value success">{history.summary.won_count}</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Lost</span>
                    <span className="stat-value danger">{history.summary.lost_count}</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Total Won</span>
                    <span className="stat-value gold">
                      {Number(history.summary.total_won).toFixed(2)}
                    </span>
                  </div>
                  {history.summary.unclaimed > 0 && (
                    <div className="summary-stat highlight">
                      <span className="stat-label">
                        <FaMoneyBillWave /> Unclaimed
                      </span>
                      <span className="stat-value">
                        {Number(history.summary.unclaimed).toFixed(2)} $XPUNT
                      </span>
                    </div>
                  )}
                </div>
              )}

              {history.items.length === 0 ? (
                <div className="vh2h-feed-empty">
                  <FaHistory className="empty-icon" />
                  <h3>No games played yet</h3>
                  <p>Join your first virtual match to see it here.</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setActiveTab('all')}
                    type="button"
                  >
                    Browse All Games
                  </button>
                </div>
              ) : (
                <div className="my-games-list">
                  {history.items.map((item) => (
                    <MyGameRow key={`${item.source}-${item.stake_id}`} item={item} />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

// ==================== CARD ====================
const VirtualMatchCard = ({ game, isAuthenticated, onRefresh }) => {
  const [now, setNow] = useState(Date.now());
  const [showJoin, setShowJoin] = useState(false);
  const [outcome, setOutcome] = useState('');
  const [stake, setStake] = useState(game.min_stake || 10);
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), TICK);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    virtualH2hAPI.joinedStatus(game.id)
      .then((r) => { if (!cancelled) setJoined(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [game.id, isAuthenticated]);

  const closesInMs = game.join_closes_at
    ? new Date(game.join_closes_at + 'Z').getTime() - now
    : 0;
  const closesInSec = Math.max(0, Math.floor(closesInMs / 1000));
  const mm = String(Math.floor(closesInSec / 60)).padStart(2, '0');
  const ss = String(closesInSec % 60).padStart(2, '0');

  const isOpen = game.status === 'open' && closesInSec > 0;
  const isLive = game.status === 'live';
  const isCompleted = game.status === 'completed';
  const isRefunded = game.status === 'refunded';
  const isCancelled = game.status === 'cancelled';

  const playHref = game.source === 'creator' && game.share_slug
    ? `/virtual-h2h/play/${game.share_slug}`
    : `/virtual-h2h/${game.id}`;

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!outcome) return toast.error('Pick an outcome');
    if (stake < game.min_stake) return toast.error(`Minimum is ${game.min_stake} $XPUNT`);

    setSubmitting(true);
    try {
      const res = await virtualH2hAPI.join(game.id, { outcome, stake: Number(stake) });
      if (res.data?.success) {
        toast.success('Joined! Good luck ⚡');
        setShowJoin(false);
        setJoined({ joined: true, stake: { outcome, stake_amount: stake, status: 'active' } });
        onRefresh?.();
      } else {
        toast.error(res.data?.message || 'Failed to join');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to join');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClaim = async () => {
    try {
      const res = await virtualH2hAPI.claim(game.id);
      if (res.data?.success) {
        toast.success(`Claimed ${Number(res.data.amount).toFixed(2)} $XPUNT`);
        onRefresh?.();
      } else {
        toast.error(res.data?.message || 'Failed to claim');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to claim');
    }
  };

  return (
    <div className={`vh2h-feed-card status-${game.status}`}>
      <Link to={playHref} className="vh2h-feed-card-body">
        <div className="card-top">
          <span className={`source-badge ${game.source}`}>
            {game.source === 'system' ? (
              <><FaRobot /> System</>
            ) : (
              <><FaUserTie /> {game.creator_name || 'Creator'}</>
            )}
          </span>
          <span className={`status-pill ${game.status}`}>
            {isOpen && <><FaClock /> {mm}:{ss}</>}
            {isLive && <><FaFire /> LIVE</>}
            {isCompleted && <>✅ FT</>}
            {isRefunded && <>💸 Refunded</>}
            {isCancelled && <>❌ Cancelled</>}
          </span>
        </div>

        <div className="card-teams">
          <div className="team home">
            {game.home_club_logo && <img src={game.home_club_logo} alt="" />}
            <span>{game.home_team}</span>
            {(isLive || isCompleted) && (
              <strong className="score">{game.home_score ?? 0}</strong>
            )}
          </div>
          <span className="vs">vs</span>
          <div className="team away">
            {game.away_club_logo && <img src={game.away_club_logo} alt="" />}
            <span>{game.away_team}</span>
            {(isLive || isCompleted) && (
              <strong className="score">{game.away_score ?? 0}</strong>
            )}
          </div>
        </div>

        <div className="card-meta">
          <span className="meta-item">
            <FaUsers /> {game.total_players || 0}
          </span>
          <span className="meta-item pot">
            <FaTrophy /> {Number(game.total_pot || 0).toFixed(2)} $XPUNT
          </span>
          {game.min_stake !== undefined && (
            <span className="meta-item min-stake">
              Min: {game.min_stake}
            </span>
          )}
        </div>
      </Link>

      {joined?.joined && (
        <div className={`your-pick status-${joined.stake?.status}`}>
          Your pick: <strong>{joined.stake?.outcome}</strong> (
          {Number(joined.stake?.stake_amount).toFixed(2)} $XPUNT)
          {joined.stake?.status === 'won' && !joined.stake?.claimed && (
            <button className="claim-btn" onClick={handleClaim} type="button">
              Claim {Number(joined.stake?.winnings).toFixed(2)} $XPUNT
            </button>
          )}
          {joined.stake?.claimed && <span className="claimed-tag">✅ Claimed</span>}
        </div>
      )}

      {isOpen && !joined?.joined && (
        <div className="vh2h-predict">
          {['Home Win', 'Draw', 'Away Win'].map((o) => (
            <button
              key={o}
              className={outcome === o ? 'active' : ''}
              onClick={() => { setOutcome(o); setShowJoin(true); }}
              type="button"
            >
              {o}
            </button>
          ))}
        </div>
      )}

      {showJoin && isOpen && !joined?.joined && (
        <form className="vh2h-join-form" onSubmit={handleJoin}>
          <label>Stake (min {game.min_stake} $XPUNT)</label>
          <input
            type="number"
            min={game.min_stake}
            step="1"
            value={stake}
            onChange={(e) => setStake(e.target.value)}
          />
          <div className="join-actions">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? <LoadingSpinner size="small" /> : `Predict ${outcome}`}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowJoin(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {isOpen && !joined?.joined && !showJoin && (
        <div className="card-cta">
          <span className="cta-text">Tap a prediction to join</span>
        </div>
      )}
    </div>
  );
};

// ==================== MY GAME ROW ====================
const MyGameRow = ({ item }) => {
  const statusMeta = {
    won:      { label: 'Won',      icon: <FaCheckCircle />, cls: 'won' },
    lost:     { label: 'Lost',     icon: <FaTimesCircle />, cls: 'lost' },
    refunded: { label: 'Refunded', icon: <FaUndo />,        cls: 'refunded' },
    active:   { label: 'Pending',  icon: <FaClock />,       cls: 'active' },
  };
  const meta = statusMeta[item.my_status] || statusMeta.active;

  const isCompleted = item.game_status === 'completed';
  const showScore = isCompleted && item.home_score !== null && item.away_score !== null;

  return (
    <Link to={item.play_url} className={`my-game-row stake-${item.my_status}`}>
      <div className="my-game-left">
        <span className={`source-dot ${item.source}`} />
        <div className="my-game-teams">
          <div className="mg-team">
            {item.home_club_logo && <img src={item.home_club_logo} alt="" />}
            <span>{item.home_team}</span>
          </div>
          {showScore ? (
            <div className="mg-score">
              <strong>{item.home_score}</strong>
              <span className="mg-score-sep">-</span>
              <strong>{item.away_score}</strong>
            </div>
          ) : (
            <span className="mg-vs">vs</span>
          )}
          <div className="mg-team away">
            <span>{item.away_team}</span>
            {item.away_club_logo && <img src={item.away_club_logo} alt="" />}
          </div>
        </div>
      </div>

      <div className="my-game-right">
        <div className="my-pick">
          <span className="my-pick-label">Your pick</span>
          <span className="my-pick-value">{item.my_outcome}</span>
        </div>
        <div className={`my-status ${meta.cls}`}>
          {meta.icon} {meta.label}
        </div>
        {item.my_status === 'won' && (
          <div className={`my-winnings ${item.my_claimed ? 'claimed' : 'unclaimed'}`}>
            +{Number(item.my_winnings).toFixed(2)} $XPUNT
            {!item.my_claimed && <span className="unclaimed-chip">Unclaimed</span>}
          </div>
        )}
        {item.my_status === 'refunded' && item.my_winnings > 0 && (
          <div className="my-winnings refunded">
            Refunded {Number(item.my_winnings).toFixed(2)} $XPUNT
          </div>
        )}
        {item.my_status === 'lost' && (
          <div className="my-winnings lost">
            -{Number(item.my_stake).toFixed(2)} $XPUNT
          </div>
        )}
      </div>
    </Link>
  );
};

export default VirtualH2H;